import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { ClickAwayListener } from '@mui/material';
import uuid from 'uuid';

import AddEditCriterionDialog from 'components/organisms/SmartGroupFormation/AddEditCriterionDialog';
import Mobile from 'components/organisms/SmartGroupFormation/mobile';
import Tablet from 'components/organisms/SmartGroupFormation/tablet';
import Desktop from 'components/organisms/SmartGroupFormation/desktop';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import { useWebsocket } from 'contexts/websocket';
import withResponsiveLayout from 'hoc/withResponsive';
import { PageContext } from 'pages/Teams/PageProvider';
import useFormValidation from 'services/hooks/useFormValidation';
import courseService from 'services/course';
import { AlertDialog, Stack, Text } from 'shell/components';
import { COUNTRY_LIST, EN_PROFICIENCY_OPTIONS, PRONOUN_OPTIONS } from 'utils/constants';
import { downloadCSV, getUserPreferredFullName } from 'utils/helper';

const worker = new Worker('/worker.js');

const STEPS = ['criteria', 'questionnaire', 'confirmation'];

const DEFAULT_FORM = {
    criteria: [
        {
            id: 'time-zone',
            name: 'Timezone',
            custom_question: '',
            weight: 3,
            field_type: 'single-select',
            mixture: 'homogeneous',
            show: true,
        },
        {
            id: 'country',
            name: 'Country',
            custom_question: '',
            weight: 3,
            field_type: 'single-select',
            mixture: 'homogeneous',
            show: true,
        },
        {
            id: 'gender',
            name: 'Pronouns',
            custom_question: '',
            weight: 3,
            field_type: 'single-select',
            mixture: 'homogeneous',
            show: true,
        },
        {
            id: 'english-proficiency',
            name: 'English Proficiency',
            custom_question: '',
            weight: 3,
            field_type: 'single-select',
            mixture: 'homogeneous',
            show: true,
        },
    ],
};

const WEIGHT_OPTIONS = [
    // { key: 0, value: 'Select' },
    { key: 5, value: '5 - Very important' },
    { key: 4, value: '4 - Important' },
    { key: 3, value: '3 - Moderately important' },
    { key: 2, value: '2 - Slightly important' },
    { key: 1, value: '1 - Not important at all' },
];

const MIXTURE_OPTIONS = [
    { key: 'homogeneous', value: 'the same group' },
    { key: 'heterogeneous', value: 'different groups' },
    { key: 'any', value: 'any groups' },
];

const getIntialStep = (groupFormation) => {
    if (!groupFormation) return STEPS[0];
    if (groupFormation?.status === 'published') return STEPS[1];
    return STEPS[0];
};

const getInitialForm = (groupFormation) => {
    return groupFormation
        ? {
              criteria: groupFormation.criteria,
              mode: groupFormation.divider.mode,
              'set-groups-value': groupFormation.divider.mode === 'set-groups' ? groupFormation.divider.value : '',
              'set-students-per-group-value':
                  groupFormation.divider.mode === 'set-students-per-group' ? groupFormation.divider.value : '',
              modified_on: groupFormation.modified_on,
          }
        : {
              ...DEFAULT_FORM,
              mode: '',
              'set-groups-value': '',
              'set-students-per-group-value': '',
              modified_on: groupFormation?.modified_on || new Date().getTime(),
          };
};

const getCriterionImportance = (weight) => {
    const match = WEIGHT_OPTIONS.find(({ key }) => key === weight);
    if (!match) return '';
    return match.key ? match.value : '';
};

const getCriterionMixture = (mixture) => {
    return MIXTURE_OPTIONS.find(({ key }) => key === mixture).value;
};

const SmartGroupFormation = () => {
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { course, studentAccountUsers } = useUser2();
    const { width } = useViewport();
    const { sendOn } = useWebsocket();

    const {
        setIsWSLoading,
        setIsGroupFormationOpen,
        groupFormation,
        setGroupFormation,
        surveys,
        setSurveys,
        setEditTeams,
    } = useContext(PageContext);

    // criteria
    const [step, setStep] = useState(getIntialStep(groupFormation));
    const [form, setForm] = useState(getInitialForm(groupFormation));
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [dialog, setDialog] = useState({ open: false, criterion: null });

    // questionnaire
    const [method, setMethod] = useState('survey');

    // confirmation
    const [filters, setFilters] = useState({ searchInput: '' });
    const [stagedGroups, setStagedGroups] = useState([]);

    // generic
    const [alertDialog, setAlertDialog] = useState({
        open: false,
        message: '',
        description: '',
        note: '',
        variant: 'destructive',
        actionButtonText: '',
        cancelButtonText: 'Cancel',
        onAction: () => {},
        onCancel: () => setAlertDialog((current) => ({ ...current, open: false })),
        onClose: () => setAlertDialog((current) => ({ ...current, open: false })),
    });

    const { errors, validate, validateAll } = useFormValidation(form, (key, value, form) => {
        if (['set-groups-value', 'set-students-per-group-value'].includes(key)) value = value?.trim();
        switch (key) {
            case 'mode':
                return ['set-groups', 'set-students-per-group'].includes(value) ? '' : 'Please select an option';
            case 'set-groups-value':
                if (form.mode !== 'set-groups') return '';
                return Number.isInteger(Number(value)) && Number(value) > 0 ? '' : 'Please enter a number';
            case 'set-students-per-group-value':
                if (form.mode !== 'set-students-per-group') return '';
                return Number.isInteger(Number(value)) && Number(value) > 0 ? '' : 'Please enter a number';
        }
    });

    const handleClickBack = () => {
        if (step === STEPS[0]) {
            if (groupFormation?.modified_on !== form.modified_on) {
                setAlertDialog((current) => ({
                    ...current,
                    open: true,
                    message: 'Changes Are Not Saved?',
                    description: 'If you leave this page, your changes will be lost.',
                    variant: 'destructive',
                    actionButtonText: 'Leave',
                    cancelButtonText: 'Cancel',
                    onAction: () => {
                        setIsGroupFormationOpen(false);
                        setAlertDialog((current) => ({ ...current, open: false }));
                    },
                }));
            } else {
                setIsGroupFormationOpen(false);
            }
        }
        if (step === STEPS[2]) {
            setAlertDialog((current) => ({
                ...current,
                open: true,
                message: 'You Have Not Confirmed Teams?',
                description:
                    'If you leave without confirming teams, all edits made will be lost. Do you want to continue?',
                variant: 'destructive',
                actionButtonText: 'Leave',
                cancelButtonText: 'Cancel',
                onAction: async () => {
                    setStep(STEPS[1]);
                    setStagedGroups([]);
                    setAlertDialog((current) => ({ ...current, open: false }));
                },
            }));
        }
    };

    const saveCriterion = (criterion) => {
        setForm((current) => {
            return {
                ...current,
                criteria: criterion.id
                    ? current.criteria.map((c) => {
                          return c.id === criterion.id ? { ...c, ...criterion } : c;
                      })
                    : [...current.criteria, { ...criterion, id: uuid.v4(), show: true }],
                modified_on: new Date().getTime(),
            };
        });
        setDialog((current) => ({ ...current, open: false }));
    };

    const handleChange = (key, value) => {
        setForm((current) => ({
            ...current,
            [key]: value,
            modified_on: new Date().getTime(),
        }));
        if (hasClickedSubmit || errors[key]) {
            validate(key, value, form);
        }
    };

    const addCriterion = () => {
        setDialog({ open: true, criterion: null });
    };

    const editCriterion = (criterion) => {
        setDialog({ open: true, criterion });
    };

    const deleteCriterion = (criterionId) => {
        setAlertDialog((current) => ({
            ...current,
            open: true,
            message: 'Delete Criterion?',
            description: 'Are you sure you want to delete this criterion?',
            variant: 'destructive',
            actionButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            onAction: () => {
                setForm((current) => ({
                    ...current,
                    criteria: current.criteria.filter(({ id }) => id !== criterionId),
                    modified_on: new Date().getTime(),
                }));
                setAlertDialog((current) => ({ ...current, open: false }));
            },
        }));
    };

    const reorderCriteria = ({ source, destination, draggableId }) => {
        const srcIndex = source.index;
        const dstIndex = destination.index;
        if (srcIndex === dstIndex) return;
        setForm((current) => {
            const newCriteria = cloneDeep(current.criteria);
            newCriteria.splice(srcIndex, 1);
            newCriteria.splice(
                dstIndex,
                0,
                current.criteria.find(({ id }) => id === draggableId)
            );
            return {
                ...current,
                criteria: newCriteria,
                modified_on: new Date().getTime(),
            };
        });
    };

    const handleSelect = ({ key }, criterion) => {
        if (key === 'edit') return editCriterion(criterion);
        if (key === 'delete') return deleteCriterion(criterion.id);
    };

    const handleSubmit = async () => {
        if (!hasClickedSubmit) setHasClickedSubmit(true);
        if (!validateAll()) return;

        const timestamp = new Date().getTime();
        const data = {
            id: groupFormation?.id || uuid.v4(),
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.owner_account_id,
            user_id: auth.user_id,
            course_id: course.id,
            criteria: form.criteria,
            divider: {
                mode: form.mode,
                value: form[`${form.mode}-value`],
            },
            status: 'published',
        };
        setForm((current) => ({ ...current, modified_on: timestamp }));
        setGroupFormation(data);
        setStep(STEPS[1]);
        await courseService.upsertCourseGroupFormation(course.id, data);
    };

    const handleSubmitDraft = async () => {
        const timestamp = new Date().getTime();
        const data = {
            id: groupFormation?.id || uuid.v4(),
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.owner_account_id,
            user_id: auth.user_id,
            course_id: course.id,
            criteria: form.criteria,
            divider: {
                mode: form.mode,
                value: form[`${form.mode}-value`],
            },
            status: 'draft',
        };
        setForm((current) => ({ ...current, modified_on: timestamp }));
        setGroupFormation(data);
        message.success('Draft saved successfully.');
        await courseService.upsertCourseGroupFormation(course.id, data);
    };

    const generateTeams = () => {
        setAlertDialog((current) => ({
            ...current,
            open: true,
            message: 'Confirm Team Generation Criteria',
            description: (
                <Stack spacing={2}>
                    <Text align={'left'} variant={'p2'}>
                        {method === 'all' ? studentAccountUsers.length : surveys.length} students will be divided into{' '}
                        {groupFormation.divider.mode === 'set-groups'
                            ? groupFormation.divider.value
                            : Math.ceil(studentAccountUsers.length / Number(groupFormation.divider.value))}{' '}
                        groups based on the following criteria:
                    </Text>
                    <Stack component={'ul'}>
                        {groupFormation.criteria
                            .filter(({ show }) => show)
                            .map(({ id, name }) => (
                                <Text key={id} align={'left'} component={'li'} variant={'p2'}>
                                    {name}
                                </Text>
                            ))}
                    </Stack>
                </Stack>
            ),
            variant: 'primary',
            actionButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                const members = studentAccountUsers
                    .filter((accountUser) => {
                        if (method === 'all') return true;
                        const survey = surveys.find(
                            ({ owner_account_id }) => owner_account_id === accountUser.owner_account_id
                        );
                        return Boolean(survey);
                    })
                    .map((accountUser) => {
                        const defaultCriteria = [];
                        if (accountUser.timezone) {
                            defaultCriteria.push({
                                id: 'time-zone',
                                value: { key: accountUser.timezone, value: accountUser.timezone },
                            });
                        }
                        if (accountUser.country) {
                            defaultCriteria.push({
                                id: 'country',
                                value: {
                                    key: accountUser.country,
                                    value: COUNTRY_LIST.find(({ code }) => code === accountUser.country).name,
                                },
                            });
                        }
                        if (accountUser.gender) {
                            defaultCriteria.push({
                                id: 'gender',
                                value: {
                                    key: accountUser.gender,
                                    value: PRONOUN_OPTIONS.find(({ value }) => value === accountUser.gender).title,
                                },
                            });
                        }
                        if (accountUser['english-proficiency']) {
                            defaultCriteria.push({
                                id: 'english-proficiency',
                                value: {
                                    key: accountUser.english_proficiency,
                                    value: EN_PROFICIENCY_OPTIONS.find(
                                        ({ value }) => value === accountUser.english_proficiency
                                    ).title,
                                },
                            });
                        }
                        return {
                            ...accountUser,
                            criteria: defaultCriteria,
                        };
                    })
                    .map((accountUser) => {
                        const survey = surveys.find(
                            ({ owner_account_id }) => owner_account_id === accountUser.owner_account_id
                        );
                        return {
                            ...accountUser,
                            criteria: [...accountUser.criteria, ...(survey?.criteria || [])],
                        };
                    });

                worker.postMessage(
                    JSON.stringify({
                        criteria: groupFormation.criteria.filter(({ show }) => show !== false),
                        members,
                        mode: groupFormation.divider.mode,
                        value: parseInt(groupFormation.divider.value),
                    })
                );

                worker.onmessage = (event) => {
                    const { data: groups } = event;
                    setStagedGroups(
                        groups.map((group) => ({ ...group, id: uuid.v4(), name: `Team ${group.groupNumber}` }))
                    );
                    setStep(STEPS[2]);
                    message.success('Your teams were successfully generated based on questionnaire criteria.');
                    setAlertDialog((current) => ({ ...current, open: false }));
                };
            },
        }));
    };

    const resetGroupFormation = () => {
        setAlertDialog((current) => ({
            ...current,
            open: true,
            message: 'Restart Team Formation?',
            description:
                'This will reset the team formation, and all data will be permanently lost. Do you want to continue?',
            variant: 'destructive',
            actionButtonText: 'Reset',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                setIsGroupFormationOpen(false);
                setGroupFormation(null);
                setSurveys([]);
                setAlertDialog((current) => ({ ...current, open: false }));
                await courseService.upsertCourseGroupFormation(course.id, { ...groupFormation, active: false });
            },
        }));
    };

    const downloadSubmissions = () => {
        const data = {
            fields: [
                'Team',
                'Student #',
                'Student',
                ...groupFormation.criteria
                    .filter(({ show }) => show)
                    .map((criterion, index) => `Q${index + 1}: ${criterion.name}`),
            ],
            data: stagedGroups
                .map((stagedGroup) => {
                    return stagedGroup.members.map((member) => {
                        return [
                            stagedGroup.groupNumber.toString(),
                            member.user_id,
                            getUserPreferredFullName(member),
                            ...groupFormation.criteria
                                .filter(({ show }) => show)
                                .map((criterion) => {
                                    const answer = member.criteria.find(({ id }) => id === criterion.id);
                                    switch (criterion.field_type) {
                                        case 'single-select':
                                            return answer?.value?.value || '';
                                        case 'multi-select':
                                            return answer ? answer.value.map(({ value }) => value).join(', ') : '';
                                        case 'likert-scale':
                                            return answer ? `${answer.value.number} - ${answer.value.value}` : '';
                                        case 'rank':
                                            return answer
                                                ? criterion.options
                                                      .map((option) => {
                                                          return {
                                                              ...option,
                                                              rank: answer.value.find(({ key }) => key === option.key)
                                                                  .value,
                                                          };
                                                      })
                                                      .sort((a, b) => Number(a.rank) - Number(b.rank))
                                                      .map(({ value }) => value)
                                                      .join(', ')
                                                : '';
                                        default:
                                            return '';
                                    }
                                }),
                        ];
                    });
                })
                .flat(),
        };
        downloadCSV(`${course.name}_questionnaire_submissions.csv`.toLowerCase().replaceAll(' ', '_'), data);
    };

    const editGroups = () => {
        const stagedStudentAccountUsers = studentAccountUsers.map((accountUser) => {
            const group = stagedGroups.find(({ members }) =>
                members.some(({ user_id }) => user_id === accountUser.user_id)
            );
            return {
                ...accountUser,
                group_id: group?.id || '',
            };
        });
        setEditTeams({
            open: true,
            type: '',
            groupSize: '',
            numGroups: '',
            groups: stagedGroups.map((group) => ({ ...group, number: group.groupNumber.toString() })),
            studentAccountUsers: stagedStudentAccountUsers,
            onSubmit: ({ teamChanges, studentChanges }) => {
                setEditTeams({
                    open: false,
                    type: '',
                    groupSize: '',
                    numGroups: '',
                    groups: [],
                    studentAccountUsers: [],
                    onSubmit: () => {},
                });
                setStagedGroups((current) => {
                    return [
                        ...current
                            .filter(({ id }) => {
                                return !teamChanges
                                    .filter(({ action }) => action === 'delete')
                                    .some(({ payload }) => payload.id === id);
                            })
                            .map((group) => {
                                const change = teamChanges.find(
                                    ({ action, payload }) => action === 'update' && payload.id === group.id
                                );
                                const members = [
                                    ...group.members.filter((member) => {
                                        if (
                                            studentChanges.find(
                                                (change) =>
                                                    change.payload.id === member.user_id &&
                                                    change.payload.from === group.id
                                            )
                                        )
                                            return false;
                                        return true;
                                    }),
                                    ...studentChanges
                                        .filter(({ payload }) => payload.to === group.id)
                                        .map(({ payload }) =>
                                            studentAccountUsers.find(({ user_id }) => user_id === payload.id)
                                        ),
                                ];
                                return {
                                    ...group,
                                    ...(change && { name: change.payload.name }),
                                    members,
                                };
                            }),
                        ...teamChanges
                            .filter(({ action }) => action === 'create')
                            .map(({ payload }) => ({
                                ...payload,
                                groupNumber: Number(payload.number),
                                members: studentChanges
                                    .filter((change) => change.payload.to === payload.id)
                                    .map((change) => {
                                        return studentAccountUsers.find(
                                            (accountUser) => accountUser.user_id === change.payload.id
                                        );
                                    }),
                            })),
                    ];
                });
            },
        });
    };

    const confirmGroups = () => {
        const teamChanges = [];
        const studentChanges = [];
        stagedGroups.forEach((group) => {
            teamChanges.push({
                type: 'group',
                action: 'create',
                payload: { id: group.id, name: group.name, number: group.groupNumber.toString() },
            });
            group.members.forEach((member) => {
                studentChanges.push({
                    type: 'member',
                    action: 'move',
                    payload: { id: member.user_id, from: null, to: group.id },
                });
            });
        });
        setIsWSLoading(true);
        sendOn('default', {
            payload_type: 'update_user_teams',
            payload: {
                course_id: course.id,
                team_changes: teamChanges,
                student_changes: studentChanges,
            },
        });
        setIsGroupFormationOpen(false);
    };

    const props = {
        groupFormation,
        surveys,
        STEPS,
        step,
        setStep,
        form,
        setForm,
        errors,

        onClickBack: handleClickBack,

        // step is criteria
        getCriterionImportance,
        getCriterionMixture,
        onChange: handleChange,
        onClick: addCriterion,
        onDragEnd: reorderCriteria,
        onSelect: handleSelect,
        onSubmit: handleSubmit,
        onSubmitDraft: handleSubmitDraft,

        // step is questionnaire
        method,
        setMethod,
        onClickGenerate: generateTeams,
        onClickReset: resetGroupFormation,

        // step is confirmation
        filters,
        setFilters,
        groups: stagedGroups,
        onClickDownload: downloadSubmissions,
        onClickEdit: editGroups,
        onClickConfirm: confirmGroups,
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    return (
        <>
            <ClickAwayListener onClickAway={() => {}}>
                {withResponsiveLayout(width, responsiveLayouts)}
            </ClickAwayListener>
            <AddEditCriterionDialog
                groupFormation={groupFormation}
                surveys={surveys}
                criterion={dialog.criterion}
                weightOptions={WEIGHT_OPTIONS}
                mixtureOptions={MIXTURE_OPTIONS}
                open={dialog.open}
                onClose={() => setDialog((current) => ({ ...current, open: false }))}
                onDelete={deleteCriterion}
                onSubmit={saveCriterion}
            />
            <AlertDialog {...alertDialog} />
        </>
    );
};

export default SmartGroupFormation;
